import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@c/icons';

type PaginatorProps = {
  currentPage: number;
  totalPages: number;
  scroll?: boolean;
  onPageChange: (page: number) => void;
};

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

const Paginator = ({ totalPages, currentPage, scroll, onPageChange }: PaginatorProps) => {
  // nothing to paginate
  if (!totalPages || totalPages < 1) return null;

  const getPageNumber = (
    direction: 'first' | 'last' | 'previous' | 'next' | number
  ) => {
    const target =
      direction === 'first' ? 1 :
        direction === 'last' ? totalPages :
          direction === 'previous' ? currentPage - 1 :
            direction === 'next' ? currentPage + 1 :
              Number(direction);

    return clamp(target, 1, totalPages);
  };

  const getPageButtons = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage < 5) return [1, 2, 3, 4, 5, '...', totalPages];
    if (currentPage === 5) return [1, '...', 4, 5, 6, '...', totalPages];
    if (currentPage < totalPages - 4)
      return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
    return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  };

  const handleClick = (page: number) => {
    const safe = clamp(page, 1, totalPages);

    if (typeof onPageChange === 'function') {
      onPageChange(safe);
    }

    if (scroll && typeof window !== 'undefined') {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
    }
  };

  return (
    <div className="flex max-w-[90vw] mx-auto items-center justify-center pt-[2rem] flex-wrap gap-[3rem] sm:gap-2">
      {/* beginning */}
      {currentPage > 1 ? (
        <>
          <button onClick={() => handleClick(getPageNumber('first'))}>
            <ChevronDoubleLeftIcon />
          </button>
          <button onClick={() => handleClick(getPageNumber('previous'))}>
            <ChevronLeftIcon />
          </button>
        </>
      ) : (
        <>
          <span className="hidden sm:flex h-touch w-touch items-center justify-center text-gray-300">
            <ChevronDoubleLeftIcon />
          </span>
          <span className="flex h-touch w-touch items-center justify-center sm:text-gray-300 text-white sm:bg-transparent bg-brand-secondary rounded-full">
            <ChevronLeftIcon />
          </span>
        </>
      )}

      {/* page numbers */}
      <div className="hidden sm:flex mx-auto">
        {getPageButtons().map((page, index) =>
          page === '...' ? (
            <span key={'ellipsis-' + index} className="flex h-touch w-touch items-center justify-center">
              ...
            </span>
          ) : (
            <button
              key={String(page)}
              onClick={() => handleClick(Number(page))}
              className={`mx-[0.4rem] flex h-touch w-touch items-center justify-center rounded-full font-semibold transition-colors ${Number(page) === currentPage ? 'bg-brand-secondary text-white' : 'bg-white text-black'
                }`}
            >
              {page}
            </button>
          )
        )}
      </div>

      {/* next */}
      {currentPage < totalPages ? (
        <>
          <button onClick={() => handleClick(getPageNumber('next'))}>
            <ChevronRightIcon />
          </button>
          <button onClick={() => handleClick(getPageNumber('last'))}>
            <span className="hidden sm:flex">
              <ChevronDoubleRightIcon />
            </span>
          </button>
        </>
      ) : (
        <>
          <span className="flex h-touch w-touch items-center justify-center text-gray-300">
            <ChevronRightIcon />
          </span>
          <span className="flex h-touch w-touch items-center justify-center text-gray-300">
            <ChevronDoubleRightIcon />
          </span>
        </>
      )}
    </div>
  );
};

export default Paginator;
